import type {
  ApplyCreditNoteRequest,
  CreateCreditNoteRequest,
  CreditNoteResponse,
  InvoiceResponse,
  PartnerItemHistory,
  PostCreditNoteRequest,
  UpdateCreditNoteRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '@palaccount/contracts';
import { Money } from '@palaccount/money';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { PARTNERS_KEY } from '@/features/partners/api';
import { RECEIPTS_KEY } from '@/features/receipts/api';
import { throwApiError } from '@/lib/server-messages';

import { INVOICES_KEY } from './api';

/** SAL-05 data layer — the credit note against a posted invoice. */

export const RETURNS_KEY = ['credit-notes'] as const;

export interface ReturnListFilters {
  partnerId?: string | undefined;
  invoiceId?: string | undefined;
  status?: CreditNoteResponse['status'] | undefined;
  /** Wave 3B — the list's window on the credit date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

/**
 * Wave 3B — whether the list draws its search box: `true` the moment the
 * contract takes `q` (the annotation flips and the typecheck demands it).
 */
export const RETURNS_SEARCHABLE: 'q' extends keyof ReturnListFilters ? true : false = false;

function returnQuery(filters: ReturnListFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.invoiceId ? { invoiceId: filters.invoiceId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** SAL-05's list, one page at a time (NFR-42). All three filters run in SQL. */
export function useCreditNotes(
  filters: ReturnListFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...RETURNS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.creditNotes.list({ query: { ...returnQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchCreditNotesPage(
  filters: ReturnListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: CreditNoteResponse[]; total: number }> {
  const response = await api.creditNotes.list({ query: { ...returnQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.creditNotes, total: response.body.total };
}

/** Wave 3B — SAL-05's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useCreditNoteStats() {
  return useQuery({
    queryKey: [...RETURNS_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.creditNotes.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * Wave 1B — what the customer bought and returned of one item (POSTED
 * documents only), read by the stand-alone return when a line names an item:
 * `lastPrice` fills the unit price, `qtySold − qtyReturned` is what the
 * over-return hint compares against. Keyed under RETURNS_KEY so posting a
 * return refreshes the returned quantity in the same breath.
 */
export function useItemHistory(partnerId: string | undefined, itemId: string | undefined) {
  return useQuery({
    queryKey: [...RETURNS_KEY, 'item-history', partnerId, itemId],
    enabled: Boolean(partnerId && itemId),
    queryFn: async (): Promise<PartnerItemHistory> => {
      const response = await api.partners.itemHistory({
        params: { partnerId: partnerId! },
        query: { itemId: itemId! },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreditNote(creditNoteId: string | undefined) {
  return useQuery({
    queryKey: [...RETURNS_KEY, 'one', creditNoteId],
    enabled: Boolean(creditNoteId),
    queryFn: async (): Promise<CreditNoteResponse> => {
      const response = await api.creditNotes.get({ params: { creditNoteId: creditNoteId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateCreditNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCreditNoteRequest): Promise<CreditNoteResponse> => {
      const response = await api.creditNotes.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RETURNS_KEY }),
  });
}

export function useUpdateCreditNote(creditNoteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateCreditNoteRequest): Promise<CreditNoteResponse> => {
      const response = await api.creditNotes.update({ params: { creditNoteId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RETURNS_KEY }),
  });
}

export function useDeleteCreditNote(creditNoteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.creditNotes.remove({ params: { creditNoteId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RETURNS_KEY }),
  });
}

/**
 * Post return — Draft → Posted: the opposite ledger entry is written and the
 * customer owes less, so partners and invoices refresh alongside.
 */
export function usePostCreditNote(creditNoteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** Wave 4A: `applyToInvoices` sets a stand-alone credit against open invoices, oldest first. */
    mutationFn: async (body: PostCreditNoteRequest = {}): Promise<CreditNoteResponse> => {
      const response = await api.creditNotes.post({ params: { creditNoteId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RETURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY });
    },
  });
}

/**
 * Wave 4A — set the posted credit against named invoices. No money moves
 * (the API's service header says why), but every invoice balance the
 * applications touch does: the invoices, the receipts' open-invoice picker,
 * the partner's past-due chip and the note itself all refresh.
 */
export function useApplyCreditNote(creditNoteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApplyCreditNoteRequest): Promise<CreditNoteResponse> => {
      const response = await api.creditNotes.apply({ params: { creditNoteId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RETURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY });
    },
  });
}

/** A customer's open POSTED invoices (balance above the F-9 tolerance), oldest first — the whole set. */
export function useCustomerOpenInvoices(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: [...INVOICES_KEY, 'open', partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<InvoiceResponse[]> => {
      const response = await api.invoices.list({
        query: { partnerId: partnerId!, openOnly: 'true', page: 1, pageSize: MAX_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      // The register lists newest first; the walk wants OLDEST first, number
      // as the tie-break (FIN-03 §3).
      return [...response.body.invoices].sort(
        (a, b) => a.invoiceDate.localeCompare(b.invoiceDate) || a.invoiceNo - b.invoiceNo,
      );
    },
  });
}

/**
 * Wave 4A — the customer's credit notes with UNAPPLIED credit: posted, not
 * refunded, and not yet set against invoices in full. The receipts screen
 * offers each as a source beside the cash grid. Keyed under RETURNS_KEY so
 * an application refreshes it in the same breath.
 */
export function useCustomerUnappliedCreditNotes(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: [...RETURNS_KEY, 'unapplied', partnerId],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<CreditNoteResponse[]> => {
      const response = await api.creditNotes.list({
        query: { partnerId: partnerId!, status: 'posted', page: 1, pageSize: MAX_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.creditNotes.filter(
        (note) =>
          note.refundedAt === null && Money.of(note.unappliedTotal, note.currency).isPositive(),
      );
    },
  });
}

/** SAL-05 §15 Q3's Refund action: the credit leaves as cash (§12 row 4). */
export function useRefundCreditNote(creditNoteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paidFromAccountId: string): Promise<CreditNoteResponse> => {
      const response = await api.creditNotes.refund({
        params: { creditNoteId },
        body: { paidFromAccountId },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RETURNS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}
